import {
  GroupTextField,
  GroupTextFieldRow,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Input,
} from "@elaniintc/goes-design-system";
import { useMaskito } from "@maskito/react";
import { duiMaskConfig, passportMaskConfig, nitMaskConfig } from "@/lib/masks";
import { useMemo } from "react";
import { cn } from "@/utils/theme";

interface VerifiedDocumentData {
  first_name?: string;
  last_name?: string;
  birth_date?: string;
  expiration_date?: string;
}

export type IdentificationFieldType = "dui" | "passport" | "nit" | "auto";

interface IdentificationFieldProps {
  documentTypeConfig: IdentificationFieldType;
  documentType: IdentificationFieldType;
  documentNumber: string;
  onDocumentTypeChange: (type: IdentificationFieldType) => void;
  onDocumentNumberChange: (number: string) => void;
  verifyDui?: boolean;
  error?: string;
  onVerificationComplete?: (data: VerifiedDocumentData | null) => void;
}

const getFieldConfig = (documentType: IdentificationFieldType) => {
  switch (documentType) {
    case "dui":
      return {
        placeholder: "00000000-0",
        maskConfig: duiMaskConfig,
      };
    case "nit":
      return {
        placeholder: "RC00012345",
        maskConfig: nitMaskConfig,
      };
    case "passport":
      return {
        placeholder: "000000000",
        maskConfig: passportMaskConfig,
      };
    default:
      return {
        placeholder: "00000000-0",
        maskConfig: duiMaskConfig,
      };
  }
};

// Componente interno que maneja la máscara
const MaskedInput = ({
  documentType,
  documentNumber,
  onChange,
  group,
}: {
  group: "left" | "right" | "single";
  documentType: IdentificationFieldType;
  documentNumber: string;
  onChange: (value: string) => void;
}) => {
  const fieldConfig = useMemo(
    () => getFieldConfig(documentType),
    [documentType],
  );
  const maskRef = useMaskito({ options: fieldConfig.maskConfig });

  return (
    <Input
      id="document-number"
      placeholder={fieldConfig.placeholder}
      className={cn("w-full")}
      group={group}
      required
      value={documentNumber}
      onChange={(e) => onChange(e.target.value)}
      ref={(el) => {
        if (el) {
          maskRef(el);
        }
      }}
    />
  );
};

export const IdentificationField = ({
  documentType,
  documentNumber,
  documentTypeConfig,
  onDocumentTypeChange,
  onDocumentNumberChange,
  error,
}: IdentificationFieldProps) => {
  const isNotAuto = documentTypeConfig !== "auto";
  return (
    <GroupTextField label="" error={error} className="w-full">
      <GroupTextFieldRow className="overflow-hidden">
        {!isNotAuto && (
          <Select
            value={documentType}
            onValueChange={(value) => {
              onDocumentTypeChange(value as IdentificationFieldType);
            }}
          >
            <SelectTrigger group="left">
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent className="border-neutral">
              <SelectItem value="dui">DUI</SelectItem>
              <SelectItem value="passport">Pasaporte</SelectItem>
              <SelectItem value="nit">NIT</SelectItem>
            </SelectContent>
          </Select>
        )}

        <div className="relative flex-auto">
          <MaskedInput
            key={`${documentType}-field`}
            documentType={documentType as IdentificationFieldType}
            documentNumber={documentNumber}
            onChange={onDocumentNumberChange}
            group={isNotAuto ? "single" : "right"}
          />
        </div>
      </GroupTextFieldRow>
    </GroupTextField>
  );
};
